<div class="file-manager-wrapper">
  <div class="card shadow-sm">

    @if (isLoading) {
      <loader [config]="loaderConfig"></loader>
    }

    <div class="card-header d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center gap-2">
        <i class="fa fa-folder-open text-primary-custom"></i>
        <h3 class="card-title mb-0">File Manager</h3>
      </div>
      <div class="breadcrumb-bar">
        <i class="fa fa-home me-1"></i>
        <span class="breadcrumb-path">{{ selectedNode?.path || currentPath }}</span>
      </div>
    </div>

    <div class="card-body p-0">
      <div class="fm-layout">

        <div class="fm-sidebar">
          <div class="fm-toolbar">
            @if (canCreateFolder && isActionAllowed('create-folder')) {
              <button
                class="fm-action-btn"
                [class.active]="currentAction === 'create-folder'"
                (click)="setAction('create-folder')"
                title="Create Folder">
                <i class="fa fa-plus"></i>
                <span>New Folder</span>
              </button>
            }

            @if (canModify && isActionAllowed('rename')) {
              <button
                class="fm-action-btn"
                [class.active]="currentAction === 'rename'"
                [disabled]="!selectedNode"
                (click)="setAction('rename')"
                title="Rename">
                <i class="fa fa-edit"></i>
                <span>Rename</span>
              </button>
            }

            @if (canModify && isActionAllowed('move')) {
              <button
                class="fm-action-btn"
                [class.active]="currentAction === 'move'"
                [disabled]="!selectedNode || isRootSelected()"
                (click)="setAction('move')"
                title="Move">
                <i class="fa fa-arrows"></i>
                <span>Move</span>
              </button>
            }

            @if (isActionAllowed('download') && selectedFile) {
              <button
                class="fm-action-btn"
                (click)="downloadFile()"
                title="Download File">
                <i class="fa fa-download"></i>
                <span>Download</span>
              </button>
            }

            @if (canDelete) {
              <button
                class="fm-action-btn fm-action-btn--danger"
                [class.active]="currentAction === 'delete'"
                [disabled]="!selectedNode"
                (click)="setAction('delete')"
                title="Delete">
                <i class="fa fa-trash"></i>
                <span>Delete</span>
              </button>
            }

            @if (canManageClearance && selectedNode) {
              <button
                class="fm-action-btn"
                (click)="openClearanceForSelected()"
                title="Permissions">
                <i class="fa fa-lock"></i>
                <span>Permissions</span>
              </button>
            }

            <div class="fm-toolbar-spacer"></div>

            @if (canModify && isActionAllowed('upload') && showUploadButton) {
              <button
                class="fm-action-btn fm-action-btn--primary"
                (click)="fileInput.click()"
                title="Upload File">
                <i class="fa fa-upload"></i>
                <span>Upload</span>
              </button>
            }
            <input #fileInput type="file" style="display:none" (change)="onFileSelected($event)">
          </div>

          <div class="fm-tree-container">
            <div class="fm-tree-node fm-tree-node--folder fm-root-node"
              [class.fm-selected]="isRootSelected()"
              [class.fm-destination]="currentAction === 'move' && isRootSelected()"
              (click)="selectRootFolder()">
              <i class="fa fa-home fm-node-icon"></i>
              <span class="fm-node-label fw-semibold">{{ initialPath }}</span>
              <span class="badge fm-badge-root ms-auto">root</span>
            </div>

            <mat-tree [dataSource]="dataSource" [treeControl]="treeControl">

              <mat-tree-node *matTreeNodeDef="let node" matTreeNodePadding
                class="fm-tree-node fm-tree-node--file"
                [class.fm-selected]="selectedNode === node"
                (click)="selectNode(node)">
                <span class="fm-node-indent"></span>
                <i class="fa fa-file-o fm-node-icon fm-node-icon--file"></i>
                <span class="fm-node-label">{{ node.name }}</span>
              </mat-tree-node>

              <mat-tree-node *matTreeNodeDef="let node; when: hasChild" matTreeNodePadding
                class="fm-tree-node fm-tree-node--folder"
                [class.fm-selected]="selectedNode === node"
                [class.fm-destination]="currentAction === 'move' && selectedDestination === node"
                (click)="selectNode(node)">
                <button class="fm-expand-btn"
                  (click)="loadFolderChildren(node); $event.stopPropagation()"
                  [attr.aria-label]="treeControl.isExpanded(node) ? 'Collapse ' + node.name : 'Expand ' + node.name">
                  <i class="fa"
                  [ngClass]="treeControl.isExpanded(node) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                </button>
                <i class="fa fm-node-icon fm-node-icon--folder"
                [ngClass]="treeControl.isExpanded(node) ? 'fa-folder-open' : 'fa-folder'"></i>
                <span class="fm-node-label">{{ node.name }}</span>
                @if (currentAction === 'move' && selectedDestination === node) {
                  <i
                  class="fa fa-check-circle fm-destination-badge ms-auto"></i>
                }
              </mat-tree-node>

            </mat-tree>

            @if (dataSource.data.length === 0) {
              <div class="fm-empty-state">
                <i class="fa fa-folder-open"></i>
                <p>This folder is empty</p>
              </div>
            }
          </div>

        </div>

        <div class="fm-panel">

          @if (alertInfo.show) {
            <ngb-alert [type]="alertInfo.type" (closed)="alertInfo.show = false" class="fm-alert">
              <i class="fa me-2"
              [ngClass]="{
                'fa-check-circle': alertInfo.type === 'success',
                'fa-exclamation-triangle': alertInfo.type === 'warning',
                'fa-times-circle': alertInfo.type === 'danger'
              }"></i>
              {{ alertInfo.message }}
            </ngb-alert>
          }

          @if (currentAction === 'move') {
            <div class="fm-panel-section">
              <div class="fm-panel-header">
                <i class="fa fa-arrows"></i>
                <span>Move {{ nodeToMove?.type === 'folder' ? 'Folder' : 'File' }}</span>
              </div>
              <div class="fm-panel-body">
                <div class="fm-info-row">
                  <span class="fm-info-label">{{ nodeToMove?.type === 'folder' ? 'Folder' : 'File' }}</span>
                  <span class="fm-info-value">
                    <i class="fa me-1" [ngClass]="nodeToMove?.type === 'folder' ? 'fa-folder' : 'fa-file-o'"></i>{{ nodeToMove?.name }}
                  </span>
                </div>
                <div class="fm-info-row">
                  <span class="fm-info-label">Destination</span>
                  <span class="fm-info-value" [class.text-muted]="!selectedDestination">
                    @if (selectedDestination) {
                      <i class="fa fa-folder me-1"></i>
                    }
                    {{ selectedDestination?.path || 'Click a folder in the tree' }}
                  </span>
                </div>
                <button class="btn fm-btn-outline w-100 mb-2" (click)="selectRootAsDestination()">
                  <i class="fa fa-home me-1"></i> Root folder — {{ initialPath }}
                </button>
                <div class="fm-panel-actions">
                  <button class="btn fm-btn-primary" [disabled]="!selectedDestination" (click)="confirmMove()">
                    <i class="fa fa-check me-1"></i> Confirm
                  </button>
                  <button class="btn fm-btn-secondary" (click)="cancelAction()">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          }

          @if (currentAction === 'delete') {
            <div class="fm-panel-section fm-panel-section--danger">
              <div class="fm-panel-header">
                <i class="fa fa-trash"></i>
                <span>Delete {{ selectedNode?.type === 'file' ? 'File' : 'Folder' }}</span>
              </div>
              <div class="fm-panel-body">
                <div class="fm-info-row">
                  <span class="fm-info-label">Name</span>
                  <span class="fm-info-value fw-semibold">{{ selectedNode?.name }}</span>
                </div>
                @if (selectedNode?.type === 'folder') {
                  <p class="fm-warning-note">
                    <i class="fa fa-exclamation-triangle me-1"></i>
                    This will delete the folder and all its contents.
                  </p>
                }
                <div class="fm-panel-actions">
                  <button class="btn fm-btn-danger" (click)="confirmDelete()">
                    <i class="fa fa-trash me-1"></i> Delete
                  </button>
                  <button class="btn fm-btn-secondary" (click)="cancelAction()">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          }

          @if (currentAction === 'create-folder' && canCreateFolder) {
            <div class="fm-panel-section">
              <div class="fm-panel-header">
                <i class="fa fa-folder-plus"></i>
                <span>New Folder</span>
              </div>
              <div class="fm-panel-body">
                <div class="fm-info-row">
                  <span class="fm-info-label">Inside</span>
                  <span class="fm-info-value">
                    <i class="fa fa-folder me-1"></i>
                    {{ (selectedNode?.type === 'folder' && selectedNode?.path) ? selectedNode!.path : currentPath }}
                  </span>
                </div>
                <div class="mb-3">
                  <label for="folderName" class="fm-input-label">Folder name</label>
                  <input id="folderName" type="text" class="form-control fm-input"
                    [(ngModel)]="newFolderName"
                    placeholder="Enter folder name"
                    (keyup.enter)="newFolderName.trim() && confirmCreateFolder()" />
                </div>
                <div class="fm-panel-actions">
                  <button class="btn fm-btn-primary" [disabled]="!newFolderName.trim()" (click)="confirmCreateFolder()">
                    <i class="fa fa-check me-1"></i> Create
                  </button>
                  <button class="btn fm-btn-secondary" (click)="cancelAction()">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          }

          @if (currentAction === 'rename') {
            <div class="fm-panel-section">
              <div class="fm-panel-header">
                <i class="fa fa-edit"></i>
                <span>Rename {{ selectedNode?.type === 'file' ? 'File' : 'Folder' }}</span>
              </div>
              <div class="fm-panel-body">
                <div class="fm-info-row">
                  <span class="fm-info-label">Current name</span>
                  <span class="fm-info-value">{{ selectedNode?.name }}</span>
                </div>
                <div class="mb-3">
                  <label for="newName" class="fm-input-label">New name</label>
                  <input id="newName" type="text" class="form-control fm-input"
                    [(ngModel)]="newName"
                    [placeholder]="selectedNode?.name || ''"
                    (keyup.enter)="newName.trim() && confirmRename()" />
                </div>
                <div class="fm-panel-actions">
                  <button class="btn fm-btn-primary" [disabled]="!newName.trim()" (click)="confirmRename()">
                    <i class="fa fa-check me-1"></i> Rename
                  </button>
                  <button class="btn fm-btn-secondary" (click)="cancelAction()">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          }

          @if (!currentAction) {
            <div class="fm-panel-section fm-panel-section--idle">
              @if (!selectedNode) {
                <div class="fm-idle-state">
                  <i class="fa fa-mouse-pointer fm-idle-icon"></i>
                  <p class="fm-idle-text">Select a file or folder to get started</p>
                </div>
              }
              @if (selectedNode) {
                <div class="fm-node-detail">
                  <div class="fm-panel-header">
                    <i class="fa" [ngClass]="selectedNode.type === 'file' ? 'fa-file-o' : 'fa-folder'"></i>
                    <span>{{ selectedNode.type === 'file' ? 'File' : 'Folder' }} details</span>
                  </div>
                  <div class="fm-panel-body">
                    <div class="fm-info-row">
                      <span class="fm-info-label">Name</span>
                      <span class="fm-info-value fw-semibold">{{ selectedNode.name }}</span>
                    </div>
                    <div class="fm-info-row">
                      <span class="fm-info-label">Type</span>
                      <span class="fm-info-value">
                        <span class="badge fm-badge-type">{{ selectedNode.type }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              }
            </div>
          }

        </div>
      </div>
    </div>
  </div>

  <file-manager-clearance-modal
    [node]="clearanceNode"
    (closed)="onClearanceClosed()">
  </file-manager-clearance-modal>
</div>
